import { useParams } from "react-router-dom";
import { Badge } from "~/components/ui/badge";
import { MarkdownPreview } from "~/components/ui/markdown-preview";
import { Skeleton } from "~/components/ui/skeleton";
import useNextGenHeader from "~/contexts/nextgen-header";
import { usePageContext } from "~/contexts/page-context";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { useFetcher } from "~/hooks/useFetcher";
import { StatusMessage } from "~/pages/manifold-nextgen/components/StatusMessage";
import { ROUTE_NEXTGEN_KB } from "~/router/constants";
import { KnowledgeArticle as Article, knowledgeArticlePath } from "~/services/knowledgeBase";

// Narrower than the nextgen list pages: this is a reading measure, not a
// layout that has to hold a table.
const ARTICLE_MAX_WIDTH = 760;

/**
 * Drops a body's opening `# Heading`.
 *
 * Every article opens with one so the file reads on its own, but the page has
 * already shown the title from the frontmatter — which is the one the index
 * lists and the agent cites, so it is the one that wins.
 */
const withoutLeadingHeading = (body: string) => body.replace(/^#\s+.*\r?\n+/, "");

/**
 * One knowledge base article, rendered as prose.
 *
 * Authored Markdown, so it uses the default link renderer — the agent-link
 * policy exists to catch paths a model invented, and nothing here is generated.
 */
export const KnowledgeArticle = () => {
  const { slug = "" } = useParams<{ slug: string }>();
  const { data: article, isLoading, error } = useFetcher<Article>(knowledgeArticlePath(slug));

  useDocumentTitle(article?.title ?? "Knowledge base");
  // Null until the title has loaded: the slug alone would describe the page in
  // terms the user never sees.
  usePageContext(article ? { page: "article", title: article.title, slug } : null);
  useNextGenHeader({
    breadcrumbs: [
      { label: "Knowledge base", href: ROUTE_NEXTGEN_KB },
      ...(article ? [{ label: article.title }] : []),
    ],
  });

  return (
    <div className="mx-auto w-full px-6 py-8" style={{ maxWidth: ARTICLE_MAX_WIDTH }}>
      {isLoading && (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-7 w-2/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-4/6" />
        </div>
      )}

      {!isLoading && (error || !article) && (
        <StatusMessage
          title="Couldn't load this article"
          body="It may have been renamed or removed."
        />
      )}

      {!isLoading && !error && article && (
        <article>
          <header className="mb-6 flex flex-col gap-2">
            <h1 className="text-2xl font-semibold text-foreground">{article.title}</h1>
            <p className="text-base text-muted-foreground">{article.summary}</p>
            {article.status === "draft" && (
              <Badge variant="warning" className="mt-1">
                Draft — not yet reviewed
              </Badge>
            )}
          </header>
          <MarkdownPreview className="prose-base">
            {withoutLeadingHeading(article.body)}
          </MarkdownPreview>
        </article>
      )}
    </div>
  );
};
